import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// ---------- types ----------
export type SavedItem = {
  discovery_id: string;
  slug: string;
  title: string;
  excerpt: string;
  image_url: string | null;
  category_label: string | null;
  saved_at: string;
};
export type HistoryItem = SavedItem & { progress: number; last_viewed_at: string };
export type UserCuriosityRow = {
  score: number;
  streak_days: number;
  longest_streak: number;
  discoveries_viewed: number;
  quizzes_completed: number;
  collections_completed: number;
  last_active_date: string | null;
};
export type ProfileRow = { id: string; username: string | null; display_name: string | null; avatar_url: string | null };
export type BadgeItem = { slug: string; name: string; description: string; icon: string | null; earned_at: string | null };

// ---------- helpers ----------
function dayDiff(a: string, b: string) {
  const da = new Date(a + "T00:00:00Z").getTime();
  const db = new Date(b + "T00:00:00Z").getTime();
  return Math.round((db - da) / 86400000);
}

// ---------- toggle save ----------
export const toggleSaveDiscovery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ discoveryId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("saved_discoveries")
      .select("discovery_id")
      .eq("user_id", userId)
      .eq("discovery_id", data.discoveryId)
      .maybeSingle();
    if (existing) {
      await supabase.from("saved_discoveries").delete().eq("user_id", userId).eq("discovery_id", data.discoveryId);
      return { saved: false };
    }
    await supabase.from("saved_discoveries").insert({ user_id: userId, discovery_id: data.discoveryId });
    return { saved: true };
  });

export const isDiscoverySaved = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ discoveryId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: row } = await context.supabase
      .from("saved_discoveries").select("discovery_id")
      .eq("user_id", context.userId).eq("discovery_id", data.discoveryId).maybeSingle();
    return { saved: !!row };
  });

// ---------- record reading view + streak ----------
export const recordDiscoveryView = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ discoveryId: z.string().uuid(), progress: z.number().int().min(0).max(100).optional() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const progress = data.progress ?? 100;

    // Upsert reading_history with incremented view_count
    const { data: prev } = await supabase
      .from("reading_history")
      .select("view_count, progress")
      .eq("user_id", userId).eq("discovery_id", data.discoveryId).maybeSingle();
    const nextCount = (prev?.view_count ?? 0) + 1;
    const nextProgress = Math.max(prev?.progress ?? 0, progress);
    await supabase.from("reading_history").upsert({
      user_id: userId,
      discovery_id: data.discoveryId,
      view_count: nextCount,
      progress: nextProgress,
      last_viewed_at: new Date().toISOString(),
    }, { onConflict: "user_id,discovery_id" });

    // Update curiosity: +5 for first view, +1 for re-view; streak handling
    const today = new Date().toISOString().slice(0, 10);
    const { data: cur } = await supabase
      .from("user_curiosity").select("*").eq("user_id", userId).maybeSingle();
    const isFirstView = !prev;
    const scoreDelta = isFirstView ? 5 : 1;

    let streak = cur?.streak_days ?? 0;
    if (!cur?.last_active_date) streak = 1;
    else {
      const diff = dayDiff(cur.last_active_date, today);
      if (diff === 0) streak = cur.streak_days;
      else if (diff === 1) streak = cur.streak_days + 1;
      else streak = 1;
    }
    const longest = Math.max(cur?.longest_streak ?? 0, streak);

    await supabase.from("user_curiosity").upsert({
      user_id: userId,
      score: (cur?.score ?? 0) + scoreDelta,
      streak_days: streak,
      longest_streak: longest,
      last_active_date: today,
      discoveries_viewed: (cur?.discoveries_viewed ?? 0) + (isFirstView ? 1 : 0),
      quizzes_completed: cur?.quizzes_completed ?? 0,
      collections_completed: cur?.collections_completed ?? 0,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" });

    // Auto-award badges
    const totalViewed = (cur?.discoveries_viewed ?? 0) + (isFirstView ? 1 : 0);
    const awards: string[] = [];
    if (totalViewed >= 1) awards.push("first-discovery");
    if (totalViewed >= 25) awards.push("curiosity-addict");
    if (totalViewed >= 100) awards.push("explorer");
    if (streak >= 7) awards.push("streak-7");
    if (streak >= 30) awards.push("streak-30");
    if (awards.length) {
      const { data: badges } = await supabase.from("badges").select("id, slug").in("slug", awards);
      if (badges?.length) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.from("user_badges").upsert(
          badges.map((b) => ({ user_id: userId, badge_id: b.id })),
          { onConflict: "user_id,badge_id", ignoreDuplicates: true },
        );
      }
    }
    return { ok: true };
  });

// ---------- record quiz ----------
export const recordQuizAttempt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({
    quizId: z.string().uuid(),
    answeredIndex: z.number().int().min(0).max(20),
  }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    // Server-side correctness check - never trust client.
    const { data: quiz } = await supabase
      .from("quizzes")
      .select("answer_index")
      .eq("id", data.quizId)
      .maybeSingle();
    if (!quiz) throw new Error("Quiz not found");
    const correct = data.answeredIndex === (quiz as any).answer_index;
    await supabase.from("quiz_history").insert({
      user_id: userId, quiz_id: data.quizId,
      answered_index: data.answeredIndex, correct,
    });
    if (correct) {
      const { data: cur } = await supabase.from("user_curiosity").select("score, quizzes_completed").eq("user_id", userId).maybeSingle();
      await supabase.from("user_curiosity").upsert({
        user_id: userId,
        score: (cur?.score ?? 0) + 10,
        quizzes_completed: (cur?.quizzes_completed ?? 0) + 1,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
      const { count } = await supabase.from("quiz_history").select("*", { count: "exact", head: true })
        .eq("user_id", userId).eq("correct", true);
      if ((count ?? 0) >= 10) {
        const { data: badge } = await supabase.from("badges").select("id").eq("slug", "quiz-master").maybeSingle();
        if (badge) {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          await supabaseAdmin.from("user_badges").upsert({ user_id: userId, badge_id: badge.id }, { onConflict: "user_id,badge_id", ignoreDuplicates: true });
        }
      }
    }
    return { ok: true, correct };
  });

// ---------- dashboard fetchers ----------
async function joinDiscoveries(rows: { discovery_id: string; [k: string]: any }[], supabase: any) {
  if (rows.length === 0) return [] as any[];
  const ids = rows.map((r) => r.discovery_id);
  const { data: discs } = await supabase
    .from("discoveries")
    .select("id, slug, title, excerpt, image_url, category_id")
    .in("id", ids);
  const { data: cats } = await supabase.from("categories").select("id, label");
  const catMap = new Map<string, string>((cats ?? []).map((c: any) => [c.id, c.label]));
  const discMap = new Map<string, any>((discs ?? []).map((d: any) => [d.id, d]));
  return rows
    .map((r) => {
      const d = discMap.get(r.discovery_id);
      if (!d) return null;
      return {
        ...r,
        discovery_id: r.discovery_id,
        slug: d.slug as string,
        title: d.title as string,
        excerpt: d.excerpt as string,
        image_url: (d.image_url as string | null) ?? null,
        category_label: d.category_id ? catMap.get(d.category_id) ?? null : null,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
}

export const getDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [profileRes, curRes, savedRes, historyRes, badgeRes, allBadgeRes] = await Promise.all([
      supabase.from("profiles").select("id, username, display_name, avatar_url").eq("id", userId).maybeSingle(),
      supabase.from("user_curiosity").select("*").eq("user_id", userId).maybeSingle(),
      supabase.from("saved_discoveries").select("discovery_id, created_at").eq("user_id", userId).order("created_at", { ascending: false }).limit(6),
      supabase.from("reading_history").select("discovery_id, progress, last_viewed_at").eq("user_id", userId).order("last_viewed_at", { ascending: false }).limit(6),
      supabase.from("user_badges").select("badge_id, earned_at").eq("user_id", userId),
      supabase.from("badges").select("id, slug, name, description, icon"),
    ]);
    const saved = await joinDiscoveries((savedRes.data ?? []).map((r: any) => ({ discovery_id: r.discovery_id, saved_at: r.created_at })), supabase);
    const history = await joinDiscoveries((historyRes.data ?? []).map((r: any) => ({
      discovery_id: r.discovery_id, progress: r.progress, last_viewed_at: r.last_viewed_at,
    })), supabase);
    const earnedMap = new Map((badgeRes.data ?? []).map((b: any) => [b.badge_id, b.earned_at]));
    const badges: BadgeItem[] = (allBadgeRes.data ?? []).map((b: any) => ({
      slug: b.slug, name: b.name, description: b.description, icon: b.icon,
      earned_at: earnedMap.get(b.id) ?? null,
    }));
    return {
      profile: (profileRes.data ?? null) as ProfileRow | null,
      curiosity: (curRes.data ?? null) as UserCuriosityRow | null,
      saved: saved as SavedItem[],
      history: history as HistoryItem[],
      badges,
    };
  });

export const listSaved = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("saved_discoveries").select("discovery_id, created_at")
      .eq("user_id", userId).order("created_at", { ascending: false });
    const items = await joinDiscoveries((data ?? []).map((r: any) => ({ discovery_id: r.discovery_id, saved_at: r.created_at })), supabase);
    return { items: items as SavedItem[] };
  });

export const listHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("reading_history").select("discovery_id, progress, last_viewed_at")
      .eq("user_id", userId).order("last_viewed_at", { ascending: false });
    const items = await joinDiscoveries((data ?? []).map((r: any) => ({
      discovery_id: r.discovery_id, progress: r.progress, last_viewed_at: r.last_viewed_at,
    })), supabase);
    return { items: items as HistoryItem[] };
  });

// ---------- recommendations (public; based on category overlap of provided discovery) ----------
export const getRecommendations = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ discoveryId: z.string().uuid(), limit: z.number().int().min(1).max(12).default(6) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: source } = await supabaseAdmin.from("discoveries").select("id, category_id").eq("id", data.discoveryId).maybeSingle();
    if (!source || !source.category_id) return { items: [] };
    const { data: items } = await supabaseAdmin
      .from("discoveries")
      .select("id, slug, title, excerpt, image_url, category_id")
      .eq("is_published", true)
      .eq("category_id", source.category_id)
      .neq("id", source.id)
      .order("created_at", { ascending: false })
      .limit(data.limit);
    return { items: items ?? [] };
  });
